/**
 * M10 P5a12d (plan `docs/plans/2026-10-17-m10-p5a.md` §3.5 card P5a12d, Q4 merged rec): on-site ore processing.
 *
 * Processing *is* the bulk compaction (spec §3.1): a processor hull (`processing.json hulls`, the Gannet) counts raw
 * bulk ore at 1× and processed tonnes (`CargoState.processed`, tonnes per ware, each ≤ `wares[w]`) at the hull's
 * `holdMult.bulk` (`career/hullFit.ts rawHoldMultOf` / `holdUsedT`), so the hold frees up as the processor works.
 * The processor turns `rateTPerH` tonnes of raw bulk ore per game hour into processed tonnes, on the flown ship and
 * on any hangar ship with a `hold` (the parked or carried hull), while the career ticks (in space). No strategic ware
 * is added: the tonnes stay in `wares`.
 *
 * `enabled` false (shipped until W6) = no processor set reaches the hold count and nothing processes: today's numbers.
 * The sale bonus (`priceMult`) is 1 by default: a processed tonne sells as raw.
 *
 * Not per-frame code: the career tick only compares `now` with `nextProcessAt` (a number); the booking below runs
 * every `stepS` game seconds and builds a new Career only when a tonne moved. Imports nothing from `hullFit` at run
 * time (`hullFit` reads `processorHulls` from here), so callers pass the processor test and the bulk mask.
 */
import { z } from 'zod'
import processingJson from '../data/processing.json'
import { WARE_IDS, type WareId } from '../data/m2Schemas'
import type { CargoState, Career, OwnedShip } from './types'

/** `src/data/processing.json` (strict: an unknown key is a typo). */
export const processingConfigSchema = z
  .object({
    comment: z.string().optional(),
    /** The whole mechanic: false = no processor set, no processing (today's behaviour). */
    enabled: z.boolean(),
    /** Hull ids (`hulls.json id`) that carry a processor. */
    hulls: z.array(z.string().min(1)),
    /** Raw bulk tonnes processed per game hour (all bulk wares together). */
    rateTPerH: z.number().positive(),
    /** Game seconds between bookings (the career tick's processing interval). */
    stepS: z.number().positive(),
    /** A processed tonne's sale price × the ore's price (≥ 1; 1 = no uplift, the merged Q4 rec). */
    priceMult: z.number().min(1),
    /** The processor's power draw [P]; read by nobody yet (the career has no live power state). */
    powerDraw: z.number().min(0),
  })
  .strict()

export type ProcessingConfig = z.infer<typeof processingConfigSchema>

let shipped: ProcessingConfig | null = null

/** The shipped processing config (parsed once). */
export function processingConfig(): ProcessingConfig {
  shipped ??= processingConfigSchema.parse(processingJson)
  return shipped
}

/** The processor hull ids the hold count reads (`HullFitData.processors`): `hulls` when enabled, else none. */
export function processorHulls(cfg: ProcessingConfig = processingConfig()): readonly string[] {
  return cfg.enabled ? cfg.hulls : []
}

/** Which M2 wares are raw bulk ore (`WARE_IDS` index → bulk), as `hullFit.bulkWareMask` gives it. */
export type BulkMask = readonly boolean[]

/** Is a ship def a processor hull (`hullFit.isProcessorDef` bound to the hull data)? */
export type IsProcessor = (defId: string) => boolean

/** The processed tonnes of `w` in `cargo`, clamped to its tonnes. */
function processedOf(cargo: Pick<CargoState, 'wares' | 'processed'>, w: WareId): number {
  return Math.min(cargo.processed?.[w] ?? 0, cargo.wares[w])
}

/** Does any ware's processed count exceed its tonnes (a sale or jettison since the last booking)? */
function overProcessed(cargo: Pick<CargoState, 'wares' | 'processed'>): boolean {
  const p = cargo.processed
  if (p === undefined) return false
  for (const w of WARE_IDS) if ((p[w] ?? 0) > cargo.wares[w]) return true
  return false
}

/** Raw (unprocessed) bulk tonnes in `cargo`. */
export function rawBulkT(cargo: Pick<CargoState, 'wares' | 'processed'>, bulk: BulkMask): number {
  let t = 0
  for (let i = 0; i < WARE_IDS.length; i++) {
    if (!bulk[i]) continue
    const w = WARE_IDS[i]
    t += cargo.wares[w] - processedOf(cargo, w)
  }
  return t
}

/**
 * `cargo` after `dtS` game seconds of processing: up to `rateTPerH × dtS / 3600` raw bulk tonnes become processed,
 * ware by ware in `WARE_IDS` order. Returns `cargo` itself when nothing moved (no raw bulk ore, or `dtS` ≤ 0), so
 * the caller can skip the Career rebuild. Every processed count is clamped to its ware's tonnes (a sale since the
 * last booking may have lowered them).
 */
export function processCargo(cargo: CargoState, dtS: number, cfg: ProcessingConfig, bulk: BulkMask): CargoState {
  let budget = dtS > 0 ? (cfg.rateTPerH * dtS) / 3600 : 0
  if ((!(budget > 0) || !(rawBulkT(cargo, bulk) > 0)) && !overProcessed(cargo)) return cargo
  const next: Partial<Record<WareId, number>> = {}
  for (let i = 0; i < WARE_IDS.length; i++) {
    const w = WARE_IDS[i]
    let p = processedOf(cargo, w)
    if (bulk[i] && budget > 0) {
      const take = Math.min(budget, cargo.wares[w] - p)
      if (take > 0) {
        p += take
        budget -= take
      }
    }
    if (p > 0) next[w] = p
  }
  return { ...cargo, processed: next }
}

/** A hangar ship's hold after `dtS` of processing (itself when nothing moved or it isn't a processor). */
function processShip(s: OwnedShip, dtS: number, cfg: ProcessingConfig, isProcessor: IsProcessor, bulk: BulkMask): OwnedShip {
  if (s.hold === undefined || !isProcessor(s.defId)) return s
  const hold = processCargo(s.hold, dtS, cfg, bulk)
  return hold === s.hold ? s : { ...s, hold }
}

/**
 * The Career after `dtS` game seconds of processing: the flown ship's hold (`career.cargo` on `career.ship.defId`)
 * and every other hangar ship's `hold` on a processor hull (the parked L, the carried S's carrier). Returns `career`
 * itself when the mechanic is off or no tonne moved.
 */
export function processCareer(career: Career, dtS: number, cfg: ProcessingConfig, isProcessor: IsProcessor, bulk: BulkMask): Career {
  if (!cfg.enabled) return career
  const cargo = isProcessor(career.ship.defId) ? processCargo(career.cargo, dtS, cfg, bulk) : career.cargo
  let hangar = career.hangar
  if (hangar !== undefined) {
    const flown = career.flownShip
    let changed = false
    const ships = hangar.map((s) => {
      const n = s.fleetId === flown ? s : processShip(s, dtS, cfg, isProcessor, bulk)
      if (n !== s) changed = true
      return n
    })
    if (changed) hangar = ships
  }
  if (cargo === career.cargo && hangar === career.hangar) return career
  return hangar === career.hangar ? { ...career, cargo } : { ...career, cargo, hangar }
}

/**
 * Does `career` have anything for the processor to do (mechanic on, and a processor hull with raw bulk ore aboard:
 * the flown ship or a hangar ship's `hold`)? The runtime arms the tick only then.
 */
export function processingPending(career: Career, cfg: ProcessingConfig, isProcessor: IsProcessor, bulk: BulkMask): boolean {
  if (!cfg.enabled) return false
  if (isProcessor(career.ship.defId) && rawBulkT(career.cargo, bulk) > 0) return true
  for (const s of career.hangar ?? []) {
    if (s.fleetId === career.flownShip || s.hold === undefined || !isProcessor(s.defId)) continue
    if (rawBulkT(s.hold, bulk) > 0) return true
  }
  return false
}

/**
 * What `tonnes` of a ware sell for at `unit` credits per tonne when `processedT` of them are processed: raw tonnes at
 * `unit`, processed tonnes at `unit × priceMult` (1 by default, so exactly `tonnes × unit`).
 */
export function processedSaleValue(tonnes: number, processedT: number, unit: number, cfg: ProcessingConfig = processingConfig()): number {
  const p = Math.min(Math.max(processedT, 0), tonnes)
  if (cfg.priceMult === 1) return tonnes * unit
  return (tonnes - p) * unit + p * unit * cfg.priceMult
}
